/**
 * What to call an SSL Certificate, in one place.
 *
 * SIX SITES NAMED CERTIFICATES SIX DIFFERENT WAYS (operator report,
 * 2026-08-21). An S/MIME certificate whose subject carries nothing but
 * an e-mail address - no common name, no organisation name, which is
 * how Sectigo® issues them - read as "Unknown SSL Certificate" in the
 * chain diagram, "N/A" on the End Entity card, "certificate" in both
 * download filenames, "(unnamed certificate)" in a link message and
 * "THIS SSL CERTIFICATE" in a verdict message. Six independent
 * fallbacks, five placeholder strings, one certificate, and its actual
 * identity sitting unread in the Subject Alternative Names the whole
 * time.
 *
 * The operator's point, and the reason this is a helper rather than a
 * seventh fallback : "a cert doesn't need a CN and may only have SANs."
 * The common name has been the optional, deprecated field for years ;
 * the Subject Alternative Names are where identity actually lives, and
 * reading them is what every client already does.
 *
 * TWO FUNCTIONS, AND THE DIFFERENCE MATTERS. certificateName is the
 * bare name and belongs anywhere the value is read as part of a
 * sentence or written into a filename. certificateDisplayName adds the
 * "+ N More SANs" count and belongs only where the name stands on its
 * own, in a card or a diagram box. Putting the display form inside a
 * sentence produces "*.trustico.com + 59 More SANs did not sign ...",
 * which is why they are separate rather than one function with a flag.
 */

/**
 * The sentence placeholder for a certificate that names nothing at all.
 *
 * LOAD-BEARING BEYOND ITS TEXT. The chain route compares link names to
 * decide whether two certificates share one identity, and two DIFFERENT
 * certificates that are both unnamed take this same string and compare
 * equal - so the route excludes it explicitly. It lives here rather
 * than beside either consumer because both the service that produces it
 * and the route that recognises it must mean the same string, and a
 * copy on either side would go silently wrong the day the other is
 * reworded.
 */
export const UNNAMED_CERTIFICATE = "(Unnamed SSL Certificate)";

/**
 * Every name a certificate carries in its Subject Alternative Names.
 *
 * Domain names, e-mail addresses and IP addresses are all identities a
 * client matches against, so all three count. parseSAN in
 * certificateService is what produces these entries.
 *
 * @param {object} info a parsed certificate
 * @returns {string[]}
 */
function subjectAltNameValues(info) {
  const entries = info?.extensions?.subjectAltName;
  if (!Array.isArray(entries)) return [];
  return entries
    .map((entry) => entry?.value)
    .filter((value) => typeof value === "string" && value.length > 0);
}

/**
 * The best bare name for a certificate, for a sentence or a filename.
 *
 * THE ORDER IS THE ORDER OF IDENTITY, not of tradition :
 *
 *   1. the common name       - still how most certificates are labelled
 *   2. the first Subject Alternative Name - where identity actually
 *      lives, and the only place it lives on an S/MIME certificate or
 *      on a certificate issued without a common name at all
 *   3. the subject e-mail address - the legacy S/MIME field, read only
 *      when the extension is absent
 *   4. the organisation name - an attribute rather than an identity,
 *      and the last thing worth showing before giving up
 *
 * The Subject Alternative Names sit ABOVE the organisation name rather
 * than below it, which is the one ordering this changes : a certificate
 * with no common name, an organisation and a name list used to be
 * labelled with the company. The list says what the certificate is FOR
 * and the company says who bought it, and the first is the more useful
 * answer.
 *
 * @param {object} info a parsed certificate
 * @returns {string|null} null when the certificate names nothing, so
 *   each caller can supply the placeholder its own context needs - a
 *   filename cannot be "(Unnamed SSL Certificate)" and a card should not
 *   say "THIS SSL CERTIFICATE"
 */
export function certificateName(info) {
  const subject = info?.subject;
  const named = (value) => typeof value === "string" && value.length > 0;

  if (named(subject?.commonName)) return subject.commonName;

  const [firstAltName] = subjectAltNameValues(info);
  if (named(firstAltName)) return firstAltName;

  if (named(subject?.emailAddress)) return subject.emailAddress;
  if (named(subject?.organizationName)) return subject.organizationName;

  return null;
}

/**
 * The name to show on a card or in a diagram box, with the count of
 * everything else the certificate covers.
 *
 * Operator ruling, 2026-08-21 : the count shows ALL the time, not only
 * when the primary name is missing. www.trustico.com carries sixty
 * names and the page said nothing about fifty-nine of them ; github.com
 * and wordpress.com carry two each.
 *
 * "SANs" IS HIS WORD, and the suffix earns its place by POINTING
 * SOMEWHERE : the Chain Analyzer carries a Subject Alternative Names
 * section, with its own explanation, listing every one of them.
 *
 * WHICH IS WHY ONLY THAT PAGE USES THIS FUNCTION (operator ruling,
 * 2026-08-21). The PFX Converter and the Certificate Matcher have no
 * such section, so a count there would leave a customer with a number,
 * an abbreviation nobody introduced, and nowhere to go ; both call
 * certificateName instead. Before adding the suffix to a fourth
 * surface, check that surface lists the names.
 *
 * COUNTED BY EXCLUSION, NOT BY SUBTRACTION. The displayed name is
 * usually also the first entry in the list, so a plain "length - 1"
 * happens to be right most of the time and is wrong for a certificate
 * whose common name is NOT among its Subject Alternative Names -
 * unusual, non-compliant, and exactly the sort of certificate someone
 * brings to this tool. Excluding the displayed value is right in both
 * cases.
 *
 * AND THE EXCLUSION IS CASE-FOLDED AND DEDUPLICATED, because the same
 * malformed certificates defeat a plain comparison just as they defeat
 * the subtraction. Domain names are case-insensitive while a subject is
 * free text, so a common name of "WWW.Example.com" beside a name list
 * holding "www.example.com" would count one OTHER name and invent a
 * name the certificate does not carry ; a list repeating an entry would
 * count it twice. The fold is for the COMPARISON only - the name shown
 * stays exactly as the certificate wrote it.
 *
 * @param {object} info a parsed certificate
 * @returns {string|null} null when the certificate names nothing
 */
export function certificateDisplayName(info) {
  const name = certificateName(info);
  if (!name) return null;

  const shown = name.toLowerCase();
  const others = new Set(
    subjectAltNameValues(info)
      .map((value) => value.toLowerCase())
      .filter((value) => value !== shown)
  ).size;
  if (others === 0) return name;

  return `${name} + ${others} More ${others === 1 ? "SAN" : "SANs"}`;
}
